import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stack, TextField } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { isValidMetadataTagId, MetadataTagRow, metadataTagLanguages } from '../../helpers/metadataTagHelpers'

type MetadataTagEditDialogProps = {
  open: boolean
  metadataTag?: MetadataTagRow
  activeIds: Set<string>
  isSaving?: boolean
  onClose: () => void
  onSave: (metadataTag: MetadataTagRow) => void
}

const emptyMetadataTag: MetadataTagRow = { id: '', names: {} }

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const MetadataTagEditDialog: FC<MetadataTagEditDialogProps> = ({ open, metadataTag, activeIds, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const isNew = !metadataTag
  const { control, handleSubmit, reset, formState } = useForm<MetadataTagRow>({
    mode: 'onChange',
    defaultValues: metadataTag ?? emptyMetadataTag,
  })

  useEffect(() => {
    if (open) {
      reset(metadataTag ?? emptyMetadataTag)
    }
  }, [open, metadataTag, reset])

  const validateId = (id: string) => {
    if (!isNew) return true
    if (!isValidMetadataTagId(id)) return t('internal-service-management:metadata_tags_id_invalid')
    if (activeIds.has(id)) return t('internal-service-management:metadata_tags_id_taken')
    return true
  }

  const submit = handleSubmit((values) => onSave(values))

  return (
    <InternalFormDialog
      open={open}
      title={isNew ? t('internal-service-management:metadata_tags_new') : t('internal-service-management:metadata_tags_edit')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="id"
          control={control}
          rules={{ validate: validateId }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={t('internal-service-management:metadata_tags_id')}
              disabled={!isNew}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || (isNew ? t('internal-service-management:metadata_tags_id_help') : undefined)}
            />
          )}
        />
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:metadata_tags_name')}
              dialogTitle={t('internal-service-management:metadata_tags_name')}
              selectedLanguages={metadataTagLanguages}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
